import { StudentName } from "../../../shared-components/StudentName";
import Grid from "@mui/material/Grid";
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Clear, Search, Visibility } from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

function StudentSummary({
  student,
  checked,
  onCheckBoxClick,
  onUnassignClicked,
}) {
  const navigate = useNavigate();

  return (
    <Card variant={"outlined"} data-testid={`student-summary-${student.id}`}>
      <CardContent>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Stack
            direction={"row"}
            spacing={2}
            alignItems={"center"}
            sx={{ mb: 1 }}
          >
            {onCheckBoxClick && (
              <Checkbox
                checked={checked}
                onChange={() => onCheckBoxClick(student.id, !checked)}
              />
            )}
            <Typography variant="subtitle1">
              <StudentName student={student} />
            </Typography>
            <Chip label={`Year ${student.yearGroup.yearGroup}`} />
          </Stack>
          {onUnassignClicked && (
            <Button
              endIcon={<Clear />}
              variant="card-action"
              onClick={() => onUnassignClicked(student)}
            >
              Unassign
            </Button>
          )}
        </Stack>

        <Typography variant="body1">ID: {student.studentIdentifier}</Typography>
        <Stack
          direction={"row"}
          sx={{ width: "100%" }}
          spacing={2}
          justifyContent={"flex-end"}
        >
          <Button
            variant={"outlined"}
            startIcon={<Visibility />}
            onClick={() => navigate(`/management/students/view/${student.id}`)}
          >
            View
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

export const FruitifyStudentSearch = ({
  students,
  selectedStudents,
  onStudentCheckBoxClick,
  onUnassignStudentClicked,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYearGroup, setSelectedYearGroup] = useState(null);

  const yearGroups = useMemo(() => {
    const set = new Map();
    students.forEach((s) => set.set(s.yearGroup.id, s.yearGroup));
    return Array.from(set.values());
  }, [students]);

  const filteredStudents = useMemo(() => {
    return students.filter((s) => {
      const lowerSearch = searchTerm.toLowerCase();

      // Match first or last name
      const matchesName =
        s.studentIdentifier?.toLowerCase().includes(lowerSearch) ||
        s.identifier.toLowerCase().includes(lowerSearch);

      // Match any of the student's classes
      const matchesClass =
        s.possibleClasses?.some((cls) =>
          cls.name.toLowerCase().includes(lowerSearch),
        ) ?? false;

      const matchesYear = selectedYearGroup
        ? s.yearGroup.id === selectedYearGroup.id
        : true;
      const matchesSearch =
        lowerSearch === "" ? true : matchesName || matchesClass;

      return matchesSearch && matchesYear;
    });
  }, [students, searchTerm, selectedYearGroup]);

  return (
    <Grid
      container
      spacing={2}
      sx={{ width: "100%" }}
      data-testid={"fruitify-teacher-search"}
    >
      {/* Each tab has the search + results */}
      <Grid size={12} sx={{ mt: 2 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{ width: "100%", mb: 2 }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 280 } }}
          />

          <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
            <InputLabel id="year-label">Year Group</InputLabel>
            <Select
              labelId="year-label"
              value={selectedYearGroup || ""}
              label="Year Group"
              onChange={(e) => setSelectedYearGroup(e.target.value || null)}
            >
              <MenuItem value="">All</MenuItem>
              {yearGroups.map((yearGroup, idx) => (
                <MenuItem key={idx} value={yearGroup}>
                  Year {yearGroup.yearGroup}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Grid>
      {filteredStudents?.map((student) => (
        <Grid size={{ xs: 12, md: 4 }} key={student.id}>
          <StudentSummary
            student={student}
            checked={selectedStudents?.includes(student.id)}
            onCheckBoxClick={onStudentCheckBoxClick}
            onUnassignClicked={onUnassignStudentClicked}
          />
        </Grid>
      ))}
    </Grid>
  );
};
